/**
 * Response shapes, mirroring `apps/api/src/routes/serialize.ts` and — from the
 * §7 section down — `apps/api/src/documents/serialize.ts`, the handler bodies in
 * `apps/api/src/documents/routes.ts`, and `apps/api/src/search/types.ts`.
 *
 * Written out rather than imported, because the API's serializers are Node-side
 * modules that reach into `@matchbox/db`; what crosses the wire is JSON, and
 * these are its types. The API deliberately hand-writes its views for the same
 * reason — a column added to the schema should not silently become a field the
 * shell renders.
 *
 * **These are hand-written mirrors, and nothing checks them.** No build step
 * compares this file against the API's serializers, so a field renamed on one
 * side and not the other type-checks on both and fails at runtime, in the
 * browser, as `undefined` where a string was expected. Two habits keep that
 * from happening: change the API view and this file in the same commit, and
 * when adding a shape here, read the handler that returns it rather than the
 * interface it appears to return — several handlers assemble their JSON inline
 * and do *not* use the exported view (see {@link DocumentVersionListItem},
 * which is `DocumentVersionView` minus `nodeId`).
 */

import type { Decision } from './client.ts';

export type UserRole = 'admin' | 'staff' | 'contractor' | 'portal_admin' | 'portal_user';
export type UserStatus = 'invited' | 'active' | 'deactivated';
export type AuthMethod = 'sso' | 'magic_link';
export type SpaceKind = 'internal' | 'client';
export type NodeType = 'document' | 'file' | 'link';
export type GrantEffect = 'allow' | 'deny';
export type GrantSubject = 'space' | 'node';
export type GrantPrincipal = 'person' | 'role';

export const USER_ROLES: readonly UserRole[] = [
  'admin',
  'staff',
  'contractor',
  'portal_admin',
  'portal_user',
];

export const USER_STATUSES: readonly UserStatus[] = ['invited', 'active', 'deactivated'];

export const NODE_TYPES: readonly NodeType[] = ['document', 'file', 'link'];

/**
 * The account half of a person's record (phase 2 §2.1) — what makes them a
 * user. Absent for a contact, and a nullable object rather than a handful of
 * fields that happen to be null together: "no access" is one fact, and
 * rendering it from one absent thing is what keeps the screens from having to
 * agree on which fields imply it.
 */
export interface UserAccount {
  id: string;
  role: UserRole;
  status: UserStatus;
  authMethod: AuthMethod;
  lastSeenAt: string | null;
  deactivatedAt: string | null;
  createdAt: string | null;
}

export interface Person {
  id: string;
  email: string;
  name: string;
  createdAt: string | null;
  account: UserAccount | null;
}

/**
 * `GET /auth/me` — narrower than a `Person`, and the only identity the shell
 * trusts. A session references an account, so the signed-in caller is always a
 * user and their role is never absent.
 */
export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
}

export interface MeResponse {
  user: SessionUser;
  session: { expiresAt: string };
}

export interface Space {
  id: string;
  name: string;
  kind: SpaceKind;
  parentSpaceId: string | null;
  isTemplate: boolean;
  createdAt: string | null;
}

export interface SpaceWithOrganization extends Space {
  organization: { id: string; name: string; spaceId: string } | null;
}

export type OrgRelationshipType = 'client' | 'prospect' | 'vendor' | 'partner' | 'internal';

export const ORG_RELATIONSHIP_TYPES: readonly OrgRelationshipType[] = [
  'client',
  'prospect',
  'vendor',
  'partner',
  'internal',
];

/** Phase 2 §3.2 — what an organization is to Matchbox, and since when. */
export interface OrganizationRelationship {
  id: string;
  organizationId: string;
  type: OrgRelationshipType;
  startedOn: string;
  /** Ends the engagement, never the container — the space outlives it (§3.3). */
  endedOn: string | null;
  ownerId: string | null;
  createdAt: string | null;
}

export interface Organization {
  id: string;
  name: string;
  parentOrgId: string | null;
  /** Null for a prospect, vendor, or partner — phase 2 §3.3. */
  spaceId: string | null;
  status: 'active' | 'archived';
  createdAt: string | null;
}

export interface OrganizationWithSpace extends Organization {
  space: Space | null;
  relationships: OrganizationRelationship[];
}

export interface TreeNode {
  id: string;
  spaceId: string;
  parentNodeId: string | null;
  type: NodeType;
  title: string;
  ownerId: string;
  position: number;
  createdAt: string | null;
  deletedAt: string | null;
}

export interface Grant {
  id: string;
  subjectType: GrantSubject;
  subjectId: string;
  principalType: GrantPrincipal;
  principalId: string;
  capability: string;
  effect: GrantEffect;
  grantedBy: string | null;
  createdAt: string | null;
}

/** `GET /api/grants` resolves the person side so a grant list renders without a second fetch. */
export type GrantPrincipalView =
  | { id: string; name: string | null; email: string | null; status?: UserStatus }
  | { role: string };

export interface GrantWithPrincipal extends Grant {
  principal: GrantPrincipalView;
}

export function isRolePrincipal(principal: GrantPrincipalView): principal is { role: string } {
  return 'role' in principal;
}

// --- people ---------------------------------------------------------------

export interface PeopleListResponse {
  people: Person[];
  total: number;
  limit: number;
  offset: number;
}

export interface PickerPerson {
  id: string;
  name: string;
  email: string;
  role: UserRole;
}

export interface PickerResponse {
  people: PickerPerson[];
  total: number;
  limit: number;
  offset: number;
}

export interface PersonDetailResponse {
  person: Person;
  memberships: Array<{
    organizationId: string;
    title: string | null;
    organizationName: string;
  }>;
  grantCount: number;
}

export interface InvitationResult {
  sent: boolean;
  error: string | null;
}

/** `POST /api/people` — a person and nothing else; no invitation is sent (§2.5). */
export interface CreatePersonResponse {
  person: Person;
}

/** `POST /api/users` — access, and the invitation that goes with it. */
export interface CreateUserResponse {
  user: Person;
  invitation: InvitationResult;
}

/** `GET /api/users` — everyone who can sign in. */
export interface UserListResponse {
  users: Person[];
  total: number;
  limit: number;
  offset: number;
}

/** `PATCH /api/users/:personId` — the role, which is the only thing it may set. */
export interface UpdateUserResponse {
  user: Person;
}

// --- deactivation (§2.6) --------------------------------------------------

export interface DeactivationBlockers {
  ownedNodes: Array<{ id: string; title: string; type: string; spaceId: string }>;
  pendingApprovals: Array<{ id: string; subjectId: string; label: string }>;
}

export interface DeactivationPreview {
  person: Person;
  blocked: boolean;
  blockers: DeactivationBlockers;
  willRevoke: { sessions: number; personGrants: number };
}

export interface DeactivationResult {
  person: Person;
  revokedSessions: number;
  revokedGrants: number;
  reassignedNodes: number;
}

// --- organizations --------------------------------------------------------

export interface OrganizationListResponse {
  organizations: OrganizationWithSpace[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * `POST /api/organizations` returns the spaces alongside the organization
 * because §3.1 makes them one creation. The screen renders them for the same
 * reason: the pair existing is only legible if the result shows it.
 *
 * All three are null for a prospect, vendor, or partner. Nothing was minted, and
 * that is the phase 2 §3.3 change rather than a failure to report.
 */
export interface CreateOrganizationResponse {
  organization: Organization;
  space: Space | null;
  sharedSpace: Space | null;
  relationships: OrganizationRelationship[];
  creatorGrants: Grant[];
}

/** `POST /api/organizations/:id/relationships` — and, for a client, the conversion. */
export interface AddRelationshipResponse {
  relationship: OrganizationRelationship;
  space: Space | null;
  sharedSpace: Space | null;
  creatorGrants: Grant[];
}

export interface OrganizationMember {
  personId: string;
  title: string | null;
  name: string;
  email: string;
  /** Null for a contact — membership is affiliation, not access (§3.2). */
  role: UserRole | null;
  status: UserStatus | null;
}

export interface OrganizationDetailResponse {
  organization: Organization;
  space: Space | null;
  /** Every one, open and closed — the closed rows are the history (§3.2). */
  relationships: OrganizationRelationship[];
  subsidiaries: Organization[];
  members: OrganizationMember[];
}

export interface SubsidiaryListResponse {
  subsidiaries: OrganizationWithSpace[];
}

export interface SubsidiaryGrantResponse {
  grants: Grant[];
  alreadyPresentOnSpaceIds: string[];
  spacesTargeted: number;
}

// --- projects (phase 2 §4) -------------------------------------------------

export type ProjectStatus = 'active' | 'on_hold' | 'complete' | 'archived';
export type ProjectRole = 'project_manager' | 'designer' | 'developer' | 'strategist' | 'qa';

export const PROJECT_STATUSES: readonly ProjectStatus[] = [
  'active',
  'on_hold',
  'complete',
  'archived',
];

export const PROJECT_ROLES: readonly ProjectRole[] = [
  'project_manager',
  'designer',
  'developer',
  'strategist',
  'qa',
];

export interface Project {
  id: string;
  name: string;
  organizationId: string;
  spaceId: string;
  status: ProjectStatus;
  leadId: string | null;
  startsOn: string | null;
  endsOn: string | null;
  createdAt: string | null;
}

export interface ProjectListResponse {
  projects: Array<Project & { space: Space; organization: Organization }>;
  total: number;
  limit: number;
  offset: number;
}

/**
 * §4.3 — one tree. `sharedWithClient` is a fact about which space the node sits
 * in, not a filter anybody applies: the shared space is a *child* of the
 * internal one, so staff see both and the badge is the only difference.
 */
export interface ProjectTreeNode {
  id: string;
  parentNodeId: string | null;
  type: NodeType;
  title: string;
  status: string | null;
  sharedWithClient: boolean;
}

export interface ProjectDetailResponse {
  project: Project;
  space: Space;
  sharedSpace: Space | null;
  organization: Organization;
  tree: ProjectTreeNode[];
  members: Array<{ personId: string; role: ProjectRole; name: string; email: string }>;
}

export interface ProjectActivityResponse {
  activity: Array<{
    id: string;
    type: string;
    subjectId: string;
    title: string | null;
    actorId: string | null;
    createdAt: string | null;
  }>;
}

export interface CreateProjectResponse {
  project: Project;
  space: Space;
  sharedSpace: Space | null;
  creatorGrants: number;
}

// --- spaces and nodes -----------------------------------------------------

export interface SpaceListResponse {
  spaces: SpaceWithOrganization[];
}

export interface SpaceDetailResponse {
  space: Space;
  children: Space[];
  organization: Organization | null;
}

export interface SpaceNodesResponse {
  space: Space;
  nodes: TreeNode[];
}

export interface CreateSpaceResponse {
  space: Space;
  creatorGrants: Grant[];
}

export interface NodeDetailResponse {
  node: TreeNode;
  children: TreeNode[];
  owner: { id: string; name: string; email: string; status: UserStatus } | null;
}

// --- grants ---------------------------------------------------------------

export interface GrantListResponse {
  grants: GrantWithPrincipal[];
}

export interface CreateGrantResponse {
  grant: Grant | null;
  created: boolean;
}

// --- effective access (§4.8) ----------------------------------------------

export interface NodeAccessEntry {
  person: { id: string; name?: string; email?: string; role?: UserRole; status?: UserStatus };
  decision: Decision;
  explanation: string;
}

export interface NodeAccessResponse {
  node: { id: string; title: string; spaceId: string; type: NodeType };
  capability: string;
  entries: NodeAccessEntry[];
}

export interface PersonAccessEntry {
  capability: string;
  node: { id: string; title?: string; type?: NodeType; spaceId?: string };
  space: { id: string; name: string; kind: SpaceKind } | null;
  decision: Decision;
  explanation: string;
}

export interface PersonAccessResponse {
  person: {
    id: string;
    name: string;
    email: string;
    role: UserRole;
    status: UserStatus;
  };
  entries: PersonAccessEntry[];
}

// --- notifications (§7.4) -------------------------------------------------

export interface NotificationView {
  id: string;
  category: string;
  title: string;
  detail: string | null;
  path: string | null;
  createdAt: string;
  readAt: string | null;
  event: {
    id: string;
    type: string;
    actorName: string | null;
    actorKind: string;
    subjectType: string;
    subjectId: string;
    subjectLabel: string | null;
    createdAt: string;
  };
}

export interface NotificationListResponse {
  notifications: NotificationView[];
  unreadCount: number;
  nextCursor: string | null;
}

// --- documents: enums (phase 1 §7.1, §7.4) --------------------------------

/**
 * Four of the shapes below narrow a field the API declares as `string`.
 *
 * `documentView` in `apps/api/src/documents/serialize.ts` types `type` and
 * `status` as `string` because it is handed a Drizzle row and widens on the way
 * out; the column underneath is a Postgres enum, so the only values that can
 * ever cross the wire are the ones listed here. Narrowing is therefore accurate
 * rather than optimistic — and it is what makes a `switch` over a status
 * exhaustive, which is the difference between a new enum member being a
 * compile error here and being a blank badge in production. `search/types.ts`
 * already narrows the same two fields for the same reason.
 *
 * The narrowed fields: `DocumentView.type` / `.status`, `LapsedReview.status` /
 * `.documentType`, `AuthorView.status`, and the `spaceKind` / `type` / `effect`
 * fields on {@link PublishPlan}.
 */
export type DocumentType =
  | 'playbook'
  | 'reference'
  | 'project_note'
  | 'status_update'
  | 'deliverable';

export const DOCUMENT_TYPES: readonly DocumentType[] = [
  'playbook',
  'reference',
  'project_note',
  'status_update',
  'deliverable',
];

/**
 * §7.2 — `document_review_by_reviewable_type` confines `review_by` to these
 * two. A review date on anything else is a check violation, so a type picker
 * that offers the field for a `status_update` is offering a 400.
 */
export const REVIEWABLE_DOCUMENT_TYPES: readonly DocumentType[] = ['playbook', 'reference'];

/**
 * §7.1, via `document_type_allowed_in` — the only types that may sit in a
 * client space, and therefore the only ones publish can move anywhere. The rest
 * publish in place (see `publish.ts`), which is a real publish and not a no-op.
 */
export const CLIENT_FACING_DOCUMENT_TYPES: readonly DocumentType[] = [
  'status_update',
  'deliverable',
];

/**
 * `in_review` is reachable as of 1b: a document enters it when an approval
 * request is opened on it (§7.6, §7.4), and leaves it when the request is
 * rejected or expired, or when the approved draft is published. Nothing in the
 * SPA sets it directly — `assertReachableStatus` on the server rejects that, on
 * the grounds that the state without its request row is a gate with nothing
 * behind it — so this is a status to render, never one to send.
 */
export type DocumentStatus = 'draft' | 'in_review' | 'published' | 'needs_review' | 'archived';

/**
 * Where a document stands against its review date (§7.2), reported alongside
 * `status` rather than inferred from it — the status says what the lifecycle
 * thinks and this says what the calendar thinks, and between the date passing
 * and the 07:00 sweep running they disagree.
 */
export type ReviewState = 'none' | 'scheduled' | 'lapsed';

/** Which of a document's three possible bodies a response is carrying (§7.4). */
export type ContentSource = 'draft' | 'published' | 'version';

/** How someone came to be on the byline (§7.3). */
export type AuthorSource = 'versions' | 'manual';

// --- documents: views ------------------------------------------------------

export interface DocumentView {
  nodeId: string;
  spaceId: string;
  parentNodeId: string | null;
  position: number;
  title: string;
  type: DocumentType;
  status: DocumentStatus;
  ownerId: string;
  publishedVersionId: string | null;
  reviewBy: string | null;
  reviewState: ReviewState;
  /**
   * §7.4 — "editors with access see the draft". True means editors are seeing
   * something readers are not.
   */
  hasWorkingDraft: boolean;
  draftUpdatedAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface DocumentContentView {
  source: ContentSource;
  /** Null when the body is the working draft, which has no version of its own. */
  versionId: string | null;
  versionNumber: number | null;
  /**
   * Canonical Tiptap/ProseMirror JSON (§5.1, ADR 0002). Typed loosely on
   * purpose: the schema that validates it lives in
   * `@matchbox/shared/markdown/schema`, and a second structural type here would
   * be a second definition of the closed component set.
   */
  content: Record<string, unknown>;
  markdown: string;
}

export interface DocumentVersionView {
  id: string;
  nodeId: string;
  versionNumber: number;
  authorId: string;
  authorName: string | null;
  createdAt: string | null;
  published: boolean;
}

/**
 * A row of `GET /api/documents/:id/versions`.
 *
 * Not a `DocumentVersionView`: that handler builds its rows inline from
 * `listVersions`, which never selects `node_id` and whose `created_at` is
 * non-null. Typing the list as `DocumentVersionView[]` would promise a
 * `nodeId` that is not there.
 */
export interface DocumentVersionListItem {
  id: string;
  versionNumber: number;
  authorId: string;
  authorName: string | null;
  createdAt: string;
  /** True for the version readers currently resolve to (§7.4). */
  published: boolean;
}

export interface AuthorView {
  personId: string;
  name: string | null;
  email: string | null;
  status: UserStatus | null;
  source: AuthorSource;
  /** Snapshots this person wrote. Zero for a manually credited contributor. */
  versionCount: number;
  firstContributionAt: string | null;
  lastContributionAt: string | null;
  /** The reason given when a person was credited by hand. */
  note: string | null;
  addedById: string | null;
}

/** The owner, resolved so a document page renders a name without a second fetch. */
export interface DocumentOwner {
  id: string;
  name: string;
  email: string;
  status: UserStatus;
}

// --- documents: responses --------------------------------------------------

export interface DocumentResponse {
  document: DocumentView;
  body: DocumentContentView;
  owner: DocumentOwner | null;
  viewer: {
    /** §7.4 — whether this caller is one of "editors with access". */
    seesWorkingDraft: boolean;
    /**
     * §7.7 — resolved server-side, because the client has no other honest source
     * for it. Presentation only: it decides whether to draw a button, never
     * whether an action is allowed.
     */
    canPublish: boolean;
  };
}

export interface CreateDocumentResponse {
  document: DocumentView;
  node: TreeNode;
  /** Always the fresh working draft; a new document has no version yet. */
  body: DocumentContentView;
}

/**
 * `null` when the save changed only the title or the review date — the version
 * history records content, and a metadata edit is not a revision of the body.
 */
export interface UpdateDocumentResponse {
  document: DocumentView;
  version: { id: string; versionNumber: number; coalesced: boolean } | null;
}

export interface ArchiveDocumentResponse {
  document: DocumentView;
}

// --- documents: publish (§7.4, ADR 0004) ----------------------------------

export interface AudienceMember {
  personId: string;
  name: string;
  email: string;
  role: UserRole;
}

export interface MovingNode {
  nodeId: string;
  title: string;
  type: NodeType;
  /** Soft-deleted descendants move too; the composite key does not exempt them. */
  deleted: boolean;
}

/** A grant sitting on a moving node — what ADR 0004 means by "re-evaluate". */
export interface PublishNodeOverride {
  grantId: string;
  nodeId: string;
  principalType: GrantPrincipal;
  principalId: string;
  capability: string;
  effect: GrantEffect;
}

/**
 * What the confirmation must state (§7.4 — "a confirmation naming the
 * audience").
 *
 * Computed by running the publish inside a transaction and rolling it back, so
 * the dialog and the outcome are the same code. A screen that renders fewer of
 * these fields than the API sends is a screen that quietly stops naming the
 * audience: `externalLinks` is §4.6's mitigation and `audience.gains` /
 * `audience.loses` are the whole point of the confirmation.
 */
export interface PublishPlan {
  nodeId: string;
  title: string;
  documentType: DocumentType;
  from: { spaceId: string; spaceName: string; spaceKind: SpaceKind };
  to: {
    spaceId: string;
    spaceName: string;
    spaceKind: SpaceKind;
    parentNodeId: string | null;
  };
  /** True when the node is already where publishing would put it — a revision. */
  alreadyInDestination: boolean;
  moving: MovingNode[];
  /** §4.6 — links travelling into a client space, which nothing can enforce access on. */
  externalLinks: Array<{ nodeId: string; title: string; url: string }>;
  nodeOverrides: PublishNodeOverride[];
  audience: {
    /** Everyone who resolves `document.view` on the node after the move. */
    after: AudienceMember[];
    gains: AudienceMember[];
    loses: AudienceMember[];
  };
  /** Whether the working draft would be snapshotted as a new version. */
  willSnapshot: boolean;
  /** §7.6 — whether editorial approval has cleared, and who it is waiting on. */
  approval: PublishApproval;
}

/**
 * The §7.6 gate as the confirmation sees it.
 *
 * `mayPublish` false is not an error state to hide: the confirmation is most
 * useful *before* the gate clears, because "waiting on Priya" is what the author
 * needs to read. The POST is what refuses, with `explanation` as its body.
 *
 * `outstandingAdvisory` is rendered beside the required set rather than folded
 * into it, and the distinction is load-bearing rather than cosmetic — §7.6's
 * whole reason for a `requirement` column is "legal should weigh in but isn't a
 * gate", and a dialog that listed both under one heading would have people
 * chasing a signature that was never going to block them.
 */
export interface PublishApproval {
  mayPublish: boolean;
  /**
   * Why not, when not. Each value is a different fix, which is why it is an enum
   * and not a boolean beside a message.
   */
  blockedBy:
    | 'never_requested'
    | 'awaiting_required'
    | 'no_required_reviewers'
    | 'rejected'
    | 'expired'
    | 'draft_changed'
    | null;
  explanation: string;
  requestId: string | null;
  outstandingRequired: Array<{ personId: string; name: string | null }>;
  outstandingAdvisory: Array<{ personId: string; name: string | null }>;
}

export interface PublishConfirmationResponse {
  confirmation: PublishPlan;
}

/**
 * The POST's report carries the destination but *not* `plan.from` — the origin
 * space is not in this body. A caller that needs to refresh the space it
 * published out of has to have remembered it (see `usePublishDocument`, which
 * sidesteps the question by invalidating every space tree).
 */
export interface PublishResponse {
  document: DocumentView;
  node: TreeNode;
  published: {
    versionId: string;
    versionNumber: number;
    to: PublishPlan['to'];
    moved: MovingNode[];
    audience: PublishPlan['audience'];
    externalLinks: PublishPlan['externalLinks'];
  };
}

// --- documents: unpublish (phase 2 §4.4) -----------------------------------

/**
 * One reason the client is already reading this — a comment, or a signature.
 *
 * Rendered rather than counted: "2 people have engaged" is a number somebody
 * has to go and investigate, and the names are what tell the operator whether
 * archiving is obviously right or whether something has gone wrong.
 */
export interface EngagementRecord {
  kind: 'comment' | 'acknowledgment';
  /** Not always the document being withdrawn — a child travels with its parent. */
  nodeId: string;
  nodeTitle: string;
  personId: string;
  personName: string;
  userRole: UserRole | 'none';
  at: string;
}

export interface ClientEngagement {
  engaged: boolean;
  comments: EngagementRecord[];
  acknowledgments: EngagementRecord[];
  /** Written to be shown to a person — it is also the body of the 409. */
  explanation: string;
}

/**
 * What the unpublish confirmation states.
 *
 * The same shape as {@link PublishPlan} minus the parts that only apply going
 * out: there is no approval gate on the way back (§4.4 — what changes is who
 * can find the document, not what it says) and nothing is snapshotted, so
 * `willSnapshot` would be a field that is always false.
 *
 * `audience.loses` is the field this direction exists to show. Publishing is
 * mostly about who gains; withdrawing is entirely about who stops being able to
 * read something they could read a moment ago.
 */
export interface UnpublishPlan {
  nodeId: string;
  title: string;
  documentType: DocumentType;
  from: { spaceId: string; spaceName: string; spaceKind: SpaceKind };
  to: {
    spaceId: string;
    spaceName: string;
    spaceKind: SpaceKind;
    parentNodeId: string | null;
  };
  moving: MovingNode[];
  externalLinks: Array<{ nodeId: string; title: string; url: string }>;
  nodeOverrides: PublishNodeOverride[];
  audience: {
    after: AudienceMember[];
    gains: AudienceMember[];
    loses: AudienceMember[];
  };
  /** §4.4 — whether this is refused, and what the refusal would strand. */
  engagement: ClientEngagement;
}

export interface UnpublishConfirmationResponse {
  confirmation: UnpublishPlan;
}

export interface UnpublishResponse {
  document: DocumentView;
  node: TreeNode;
  unpublished: {
    to: UnpublishPlan['to'];
    moved: MovingNode[];
    audience: UnpublishPlan['audience'];
    externalLinks: UnpublishPlan['externalLinks'];
  };
}

// --- documents: versions (§7.5) -------------------------------------------

export interface VersionListResponse {
  versions: DocumentVersionListItem[];
  total: number;
  limit: number;
  offset: number;
}

/** `created: false` means the snapshot was identical to the newest version. */
export interface CreateVersionResponse {
  version: DocumentVersionView;
  created: boolean;
}

export interface VersionDetailResponse {
  version: DocumentVersionView;
  body: DocumentContentView;
}

/**
 * A restore writes the working draft and snapshots it. It does **not** repoint
 * `published_version_id` — readers keep seeing what they were seeing until
 * someone publishes (§7.4, §7.5).
 */
export interface RestoreVersionResponse {
  document: DocumentView;
  restoredFrom: { versionId: string; versionNumber: number };
  /** The snapshot the restore itself created, so the history says it happened. */
  version: DocumentVersionView;
}

// --- documents: diffs (§7.5, §5.1) ----------------------------------------

export type DiffLineType = 'context' | 'added' | 'removed';

export interface DiffLine {
  type: DiffLineType;
  text: string;
  /** 1-based line number on the left side, or null for an added line. */
  fromLine: number | null;
  /** 1-based line number on the right side, or null for a removed line. */
  toLine: number | null;
}

/** One run of changes with its surrounding context — a unified-diff `@@` block. */
export interface DiffHunk {
  fromStart: number;
  fromLines: number;
  toStart: number;
  toLines: number;
  lines: DiffLine[];
}

export interface MarkdownDiff {
  hunks: DiffHunk[];
  added: number;
  removed: number;
  /** True when the two sides are byte-identical, in which case `hunks` is empty. */
  identical: boolean;
  /**
   * True when the pair was too large to align line by line and the whole body
   * is reported as one replacement — honest and useless, rather than a wrong
   * alignment, which would be useless and convincing.
   */
  truncated: boolean;
  /** The same result as unified-diff text, for copy-paste. */
  unified: string;
}

/** A version id, the working draft, or whatever is currently published. */
export type DiffRevisionRef = string | 'draft' | 'published';

export interface DiffRevision {
  ref: string;
  versionId: string | null;
  versionNumber: number | null;
}

export interface DiffResponse {
  from: DiffRevision;
  to: DiffRevision;
  diff: MarkdownDiff;
}

// --- documents: authorship (§7.3) -----------------------------------------

export interface AuthorsResponse {
  authors: AuthorView[];
  /** §7.3 — the owner is singular, and is not part of the derived byline. */
  ownerId: string;
}

/** The add and remove routes return only the recomputed byline, with no `ownerId`. */
export interface AuthorMutationResponse {
  authors: AuthorView[];
}

// --- documents: review dates (§7.2) ---------------------------------------

export interface LapsedReview {
  nodeId: string;
  title: string;
  documentType: DocumentType;
  status: DocumentStatus;
  ownerId: string;
  ownerName: string | null;
  spaceId: string;
  reviewBy: string;
}

/**
 * `scope` is echoed back because the two scopes answer different questions:
 * `mine` is §7.2's nag and the default, `all` is §7.7's cross-document
 * compliance view.
 */
export interface ReviewDueResponse {
  documents: LapsedReview[];
  total: number;
  scope: 'mine' | 'all';
}

// --- search (§9) -----------------------------------------------------------

/** Documents, files, and links — the three §4.2 node types, all searchable. */
export type SearchKind = NodeType;

export interface SearchHitBase {
  nodeId: string;
  spaceId: string;
  title: string;
  ownerId: string;
  /** The payload's `updated_at`, or the node's creation instant if it has none. */
  updatedAt: string;
  rank: number;
  /**
   * False when nothing in the row matched the query terms and the row is here
   * because its title *resembles* them (§9.1's typo tolerance). Every hit with
   * this true sorts ahead of every hit with it false, so the flag is also the
   * boundary between two tiers a result list may want to label differently.
   */
  matchedText: boolean;
}

export interface DocumentHit extends SearchHitBase {
  kind: 'document';
  document: {
    /** Null only for a node whose payload row has not been written yet. */
    type: DocumentType | null;
    status: DocumentStatus;
    reviewBy: string | null;
    /** §7.4 — editors are seeing something readers are not. */
    hasDraft: boolean;
  };
  /**
   * A relevant fragment of the body, plain text. Null when there is no payload.
   *
   * Plain text with no markup, deliberately: `ts_headline` is configured with
   * empty `StartSel`/`StopSel` so that rendering a search result never needs
   * `dangerouslySetInnerHTML` on the one surface that shows text from every
   * document a person can reach.
   */
  snippet: string | null;
}

export interface FileHit extends SearchHitBase {
  kind: 'file';
  /**
   * Null for a file node whose bytes have not landed yet — the node is still
   * findable by title.
   *
   * There is no `objectKey` here and there never will be: phase-0 §5.1 keeps it
   * server-side, and a type that never had the field cannot leak it.
   */
  file: {
    filename: string;
    contentType: string;
    sizeBytes: number;
    uploadedById: string;
    uploadedByName: string;
  } | null;
}

export interface LinkHit extends SearchHitBase {
  kind: 'link';
  link: { url: string } | null;
}

export type SearchHit = DocumentHit | FileHit | LinkHit;

export interface SearchResults {
  /** Echoed back so a client rendering "no results for X" cannot disagree with the query. */
  query: string;
  /**
   * How many rows the ACL and the status rules left, before paging. Counted
   * inside the same statement the page is drawn from (§9.2) — a count from a
   * second query is how result counts leak the existence of documents.
   */
  total: number;
  limit: number;
  offset: number;
  results: SearchHit[];
}

/**
 * Narrowing helpers, because the union discriminates on `kind` and a list that
 * renders all three types will reach for these in a `.filter` before TypeScript
 * will narrow through one.
 */
export function isDocumentHit(hit: SearchHit): hit is DocumentHit {
  return hit.kind === 'document';
}

export function isFileHit(hit: SearchHit): hit is FileHit {
  return hit.kind === 'file';
}

export function isLinkHit(hit: SearchHit): hit is LinkHit {
  return hit.kind === 'link';
}

// --- approvals (§7.6) ------------------------------------------------------

/**
 * `apps/api/src/approvals/routes.ts`, whose serializers these mirror.
 *
 * The lifecycle §7.6 names, and the subject is polymorphic on purpose — Phase
 * 4's client acceptance lands on this same table, which is why the wire format
 * spells it `subject: { type, id }` rather than `documentId`. A client that
 * flattened it here would have to be versioned the day that arrives.
 */
export type ApprovalStatus = 'requested' | 'pending' | 'approved' | 'rejected' | 'expired';

/**
 * The distinction the whole of §7.6 exists to draw: "publish unblocks when all
 * **required** reviewers approve; advisory reviewers may remain outstanding".
 * Any screen that renders a reviewer without rendering this has thrown the
 * feature away and kept the table.
 */
export type ReviewerRequirement = 'required' | 'advisory';

/** Null on a reviewer means outstanding — the state advisory reviewers may stay in forever. */
export type ReviewerDecision = 'approved' | 'rejected';

/**
 * Why publish is refused. Every value is a different fix, which is why the API
 * sends an enum and a sentence rather than a boolean and a sentence — the
 * sentence is {@link ApprovalGate.explanation} and is the server's to write.
 */
export type ApprovalBlock =
  | 'never_requested'
  | 'awaiting_required'
  | 'no_required_reviewers'
  | 'rejected'
  | 'expired'
  | 'draft_changed';

export interface ApprovalReviewer {
  personId: string;
  /** Null when the person row is gone; the id is then the only name there is. */
  name: string | null;
  email: string | null;
  /**
   * §2.6 — "a departed reviewer has to read as departed, not as silent". The
   * API types this as a bare string; the values are the `person.status` enum.
   */
  userStatus: UserStatus | null;
  requirement: ReviewerRequirement;
  decision: ReviewerDecision | null;
  decidedAt: string | null;
  addedAt: string;
  /**
   * The row §7.3's auto-add put here, resolved against `node.owner_id` as it
   * stands *now* — so a document that changed hands mid-review shows the old
   * owner as an ordinary required reviewer. It refuses a removal and decides
   * nothing else.
   */
  isOwner: boolean;
}

export interface ApprovalRequest {
  id: string;
  subject: { type: 'document'; id: string };
  status: ApprovalStatus;
  /** `requested` or `pending` — the two states the one-open-request index covers. */
  open: boolean;
  createdBy: string;
  createdAt: string;
  resolvedAt: string | null;
  /** Every reviewer, decided or not, in the order they were added. */
  reviewers: ApprovalReviewer[];
}

/**
 * The publish gate for one document, as `GET /api/approvals/documents/:id`
 * returns it and as every mutation returns it alongside the request.
 *
 * `requestId` is the request the gate is *about*, which is the most recent one
 * on the document and not necessarily the one being looked at. A screen showing
 * a superseded request has to say so rather than paint this gate as its state.
 */
export interface ApprovalGate {
  nodeId: string;
  requestId: string | null;
  status: ApprovalStatus | null;
  mayPublish: boolean;
  blockedBy: ApprovalBlock | null;
  /** Written to be shown to a person — it is also the body of publish's 409. */
  explanation: string;
  outstandingRequired: ApprovalReviewer[];
  rejectedRequired: ApprovalReviewer[];
  /** Reported and never blocking — §7.6's "legal isn't a gate", made visible. */
  outstandingAdvisory: ApprovalReviewer[];
}

/**
 * What every mutating approval route answers with: the request, and the gate
 * recomputed inside the same transaction. Two reviewers deciding at once would
 * otherwise each be told they were the last one.
 */
export interface ApprovalMutationResponse {
  request: ApprovalRequest;
  gate: ApprovalGate;
}

export interface ApprovalRequestResponse {
  request: ApprovalRequest;
}

export interface ApprovalsAwaitingResponse {
  requests: ApprovalRequest[];
  total: number;
  /**
   * Requests this person still reviews and can no longer open. Reported rather
   * than hidden: a required row nobody can clear is what deadlocks a document,
   * and this count is how the fact surfaces without naming the document.
   */
  unreachable: number;
}

export interface DocumentApprovalsResponse {
  requests: ApprovalRequest[];
  total: number;
  gate: ApprovalGate;
}

// --- acknowledgments (§7.7) ------------------------------------------------

/**
 * How a cycle asks its question — never who it resolved to.
 *
 * §7.7 allows a role or a capability held on a space, and nothing else. There
 * is deliberately no list-of-people shape: the audience is re-resolved on every
 * read so a new hire enrols into a cycle opened before they existed, and a
 * stored membership list would be the snapshot the spec spends a paragraph
 * ruling out.
 */
export type AcknowledgmentAudienceKind = 'role' | 'space_grant';

export interface AcknowledgmentAudience {
  kind: AcknowledgmentAudienceKind;
  role: UserRole | null;
  spaceId: string | null;
  capability: string | null;
}

/**
 * N of M, where both sides are computed against the audience as it resolves
 * right now.
 *
 * `acknowledged` counts audience members who signed; `acknowledgedOutsideAudience`
 * is the people who signed and have since left it. They are reported apart
 * because folding them in produces "9 of 8" — the shape of number that makes a
 * compliance view stop being read — and dropping them would hide that the cycle
 * was once further along than it now looks.
 */
export interface AcknowledgmentProgress {
  /** M — the audience right now. */
  audienceSize: number;
  /** N. */
  acknowledged: number;
  outstanding: number;
  acknowledgedOutsideAudience: number;
  /** True for an empty audience too, which is why the size is rendered beside it. */
  complete: boolean;
}

export interface AcknowledgmentCycle {
  requestId: string;
  documentId: string;
  title: string;
  documentStatus: DocumentStatus;
  /** §7.7 — acknowledgment is scoped to a version, not a document. */
  versionId: string;
  versionNumber: number;
  audience: AcknowledgmentAudience;
  dueDate: string | null;
  createdBy: string;
  createdAt: string | null;
  /**
   * Whether this cycle can still be signed: its version is still the published
   * one *and* the document has not been archived (§7.7). Read `documentStatus`
   * to say which of the two closed it.
   */
  current: boolean;
  /**
   * **Null means "not yours to see", never "nobody has signed".** The numbers
   * go to the owner and to holders of `document.publish` on the node; everyone
   * else gets the cycle without them, because a deliverable published into a
   * client space (ADR 0004) would otherwise render "3 of 11 of the agency's
   * staff" on a client's screen. `progressVisible` on the envelope says which
   * answer this caller got.
   */
  progress: AcknowledgmentProgress | null;
  /** Where the reader stands. Always present — it is about them. */
  viewer: { inAudience: boolean; acknowledged: boolean };
}

export interface AcknowledgmentRosterEntry {
  personId: string;
  name: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  /** False for someone who signed and has since left the audience. */
  inAudience: boolean;
  acknowledgedAt: string | null;
}

export interface OwedAcknowledgment {
  requestId: string;
  documentId: string;
  title: string;
  versionId: string;
  versionNumber: number;
  dueDate: string | null;
  createdAt: string | null;
  /** Computed against today by the server, so a list can sort without a clock. */
  overdue: boolean;
}

export interface OwedAcknowledgmentsResponse {
  acknowledgments: OwedAcknowledgment[];
  total: number;
  overdue: number;
}

/** One signature this person has already given (`GET /api/acknowledgments/history`). */
export interface SignedAcknowledgment {
  requestId: string;
  documentId: string;
  title: string;
  documentStatus: DocumentStatus;
  versionId: string;
  versionNumber: number;
  acknowledgedAt: string | null;
  /**
   * False once the cycle this signature belongs to stopped being live — a
   * revision superseded it, or the document was archived. Reported rather than
   * filtered: the signature is still a true record of what was read, and any
   * replacement cycle appears under `outstanding`.
   */
  current: boolean;
}

export interface AcknowledgmentHistoryResponse {
  outstanding: OwedAcknowledgment[];
  signed: SignedAcknowledgment[];
  overdue: number;
}

export interface AcknowledgmentCycleResponse {
  cycle: AcknowledgmentCycle;
  /** Null rather than `[]` when the roster is not this caller's to see. */
  people: AcknowledgmentRosterEntry[] | null;
  progressVisible: boolean;
}

export interface DocumentAcknowledgmentsResponse {
  cycles: AcknowledgmentCycle[];
  total: number;
  progressVisible: boolean;
}

export interface OpenCycleResponse {
  cycle: AcknowledgmentCycle;
}

/** §7.7's cross-document view. Never redacted — reaching it at all is `audit.view`. */
export interface ComplianceResponse {
  cycles: AcknowledgmentCycle[];
  total: number;
  limit: number;
  offset: number;
}

export interface AcknowledgeResponse {
  acknowledgment: {
    requestId: string;
    documentId: string;
    personId: string;
    acknowledgedAt: string;
  };
  /** True when this call was a second press — §6.3, a no-op is not an action. */
  alreadyRecorded: boolean;
}
